import { Map, List, fromJS, OrderedMap } from 'immutable';

import Actions from 'flow/actions';
import { listToMap, keyIn } from 'flow/utils';
import { getVoiceMessagesSortedByReceivedDate } from 'flow/utils/call-records-utils';

const initialState = Map({
    waitingQueueItems: Map({
        byKey: Map(),
        byQueue: Map(),
    }),
    queueMembers: Map(),
});

// eslint-disable-next-line complexity
export const pbxReducer = (state = initialState, action) => {
    switch (action ? action.type : null) {
        case Actions.CLEAR_STATE: {
            return initialState;
        }
        case Actions.RESULT_QUEUE: {
            const queueDTO = fromJS(action.data);

            const reducedQueueDTO = queueDTO
                .delete('queueCalls')
                .delete('queueMembers');

            const queueMembers = queueDTO.get('queueMembers');

            return state
                .updateIn(['queues', action.key], Map(), (prev) => {
                    return prev
                        .delete('welcomeSound')
                        .delete('activeProfile')
                        .delete('queueWaves')
                        .mergeDeep(reducedQueueDTO);
                })
                .update('queueMembers', (prevQueueMembers = Map()) => {
                    if (queueMembers) {
                        return prevQueueMembers.set(action.key, queueMembers);
                    }
                    return prevQueueMembers;
                })
                .update('sortingData', (sortingData = Map()) => {
                    // The PBX list searches this projection, which RESULT_QUEUES builds.
                    // A single queue arrives here after a save or a fetch, so its keywords
                    // are refreshed when it carries its own contact; a queue the list has
                    // not projected yet is left for RESULT_QUEUES to add. (AC-1943)
                    if (
                        !queueDTO.has('contact') ||
                        !sortingData.hasIn(['queues', action.key])
                    ) {
                        return sortingData;
                    }
                    return sortingData.setIn(
                        ['queues', action.key, 'keywords'],
                        queueDTO.getIn(['contact', 'keywords']),
                    );
                });
        }
        case Actions.RESULT_TOP_WAITING_QUEUE_ITEMS_ADD: {
            let byKeyMap = state.getIn(['waitingQueueItems', 'byKey'], Map());
            let byQueueList = state.getIn(
                ['waitingQueueItems', 'byQueue', action.queueKey],
                List(),
            );
            for (const queueCall of action.queueCalls) {
                byKeyMap = byKeyMap.set(queueCall.key, fromJS(queueCall));
                byQueueList = byQueueList.push(queueCall.key);
            }
            return state
                .setIn(['waitingQueueItems', 'byKey'], byKeyMap)
                .setIn(
                    ['waitingQueueItems', 'byQueue', action.queueKey],
                    byQueueList,
                );
        }
        case Actions.RESULT_TOP_WAITING_QUEUE_ITEM_ADD: {
            const queueCall = fromJS(action.queueCall);
            const position = action.position;

            return state
                .setIn(
                    ['waitingQueueItems', 'byKey', action.queueCallKey],
                    queueCall,
                )
                .updateIn(
                    ['waitingQueueItems', 'byQueue', action.queueKey],
                    List(),
                    (list) => {
                        return list.insert(position - 1, action.queueCallKey);
                    },
                );
        }
        case Actions.RESULT_TOP_WAITING_QUEUE_ITEM_REMOVE: {
            return state.withMutations((map) => {
                map.deleteIn([
                    'waitingQueueItems',
                    'byKey',
                    action.queueCallKey,
                ]).updateIn(
                    ['waitingQueueItems', 'byQueue', action.queueKey],
                    List(),
                    (list) => {
                        const index = list.findIndex(
                            (queueCallKey) =>
                                queueCallKey === action.queueCallKey,
                        );
                        return index !== -1 ? list.delete(index) : list;
                    },
                );
            });
        }
        case Actions.SET_TOP_QUEUE_CALL: {
            return state.updateIn(
                ['topActiveQueueCalls', 'byQueue', action.queue],
                OrderedMap(),
                (map) => map.set(action.channelID, action.state),
            );
        }
        // Once an agent picks a queue call up it carries on as an ordinary channel,
        // and that channel leaving the queue's ongoing calls is the only thing left
        // to tell us the call is over. Neither the channel nor its hangup carries
        // the queue call's locutus session or when it entered the queue, so we
        // remember both against the channel while it is still there. Keyed per queue
        // since a call transferred between queues produces one record per queue.

        case Actions.SET_QUEUE_CALL_FOR_CHANNEL: {
            const path = [
                'queueCallsByChannel',
                action.channelID,
                action.queueKey,
            ];

            // The channel repeats its queue events on hold, unhold and
            // transfer. Keep the call record once it is known: a later event
            // can only match on the caller's number again, which by then can
            // reach a different caller who waits with the same number.
            if (state.getIn([...path, 'callRecordKey'])) return state;

            return state.setIn(
                path,
                Map({
                    callRecordKey: action.callRecordKey,
                    queueJoinTime: action.queueJoinTime,
                }),
            );
        }
        case Actions.RESULT_TOP_HANGUP_CHANNEL: {
            //We try to remove the UniqueID from all queues due to a problem that occurrs when a queue call is transferred to another queue
            //and then picked, then the UniqueID will be present for both queues and we need to remove both of them. This is because top first sends
            //a QueueCallInfo event with the first queue's info before it sends one with the seconds queue's infor when the call is picked.
            //This feels wrong and should probably be fixed so that the first event is not sent.
            return state
                .updateIn(
                    ['topActiveQueueCalls', 'byQueue'],
                    OrderedMap(),
                    (queues) =>
                        queues.map((map) => map.delete(action.data.UniqueID)),
                )
                .deleteIn(['queueCallsByChannel', action.data.UniqueID]);
        }
        case Actions.RESULT_ACTIVE_OMNI_ERRANDS: {
            const updatedErrands = listToMap(fromJS(action.data), (e) =>
                e.get('callId'),
            );
            return state.setIn(
                ['activeOmniErrands', 'byQueue', action.queue],
                updatedErrands,
            );
        }
        case Actions.CLEAR_TOP_QUEUE_CALLS: {
            return state
                .delete('topActiveQueueCalls')
                .delete('queueCallsByChannel')
                .deleteIn(['waitingQueueItems', 'byKey'])
                .deleteIn(['waitingQueueItems', 'byQueue']);
        }
        case Actions.RESULT_QUEUE_PROFILE: {
            return state.updateIn(['queues', action.key, 'activeProfile'], () =>
                fromJS(action.data),
            );
        }
        case Actions.RESULT_QUEUE_CALENDAR: {
            return state.updateIn(['queues', action.key, 'calendar'], () =>
                fromJS(action.data),
            );
        }
        case Actions.RESULT_QUEUE_OPENING_HOURS: {
            return state.setIn(
                ['queues', action.key, 'calendar', 'openingHours'],
                fromJS(action.data),
            );
        }
        case Actions.RESULT_QUEUE_STAT_SUMMARY: {
            return state.mergeIn(
                ['queues', action.key, 'statistics', 'queueStats'],
                fromJS(action.data).get('queueStats'),
            );
        }
        case Actions.RESULT_QUEUES: {
            const newQueues = fromJS(action.data);
            const mapped = listToMap(newQueues, (item) => item.get('key'));
            const ownExtensionKey = action.ownExtensionKey;

            // Extract queueMembers from queues and remove them from the queue objects
            const mappedWithoutQueueMembers = mapped.map((queue) =>
                queue.delete('queueMembers'),
            );

            return state
                .update('queues', (prevQueues = Map()) => {
                    return prevQueues
                        .filter(keyIn(...mapped.keys()))
                        .mergeDeep(mappedWithoutQueueMembers);
                })
                .update('queueMembers', (prevQueueMembers = Map()) => {
                    /*
                    In order to show the correct list of queuemembers when the list is modified from some
                    other client we need to explicitly remove the queuemembers that are in the state
                    but not in the updated queue data, since mergeDeep will not do that for us.
                    And since the queueMembers in the updated queue data (which is from getSimpleQueues)
                    do not contain contacts we cannot simply replace the old queuemembers with the new ones
                    becaues that would remove existing contacts, causing the behavior in WEB-8273.
                    */
                    let updatedQueueMembers = prevQueueMembers;

                    // Remove queueMembers for queues that no longer exist
                    updatedQueueMembers = updatedQueueMembers.filter(
                        (_members, queueKey) => mapped.has(queueKey),
                    );

                    // For each queue, filter out members that are no longer in the updated data
                    // and add new members that weren't there before
                    mapped.forEach((updatedQueue, queueKey) => {
                        const updatedQueueMembersFromAction = updatedQueue.get(
                            'queueMembers',
                            List(),
                        );
                        const updatedQueueMemberKeys =
                            updatedQueueMembersFromAction.map((queueMember) =>
                                queueMember.get('key'),
                            );

                        const prevQueueMembersList = updatedQueueMembers.get(
                            queueKey,
                            List(),
                        );

                        if (prevQueueMembersList.size > 0) {
                            // Merge updated data into existing members (preserves fields
                            // like contact that getSimpleQueues doesn't return, while
                            // updating fields like loggedIn that it does return)
                            const mergedQueueMembers = prevQueueMembersList
                                .filter((queueMember) =>
                                    updatedQueueMemberKeys.includes(
                                        queueMember.get('key'),
                                    ),
                                )
                                .map((prevMember) => {
                                    const updatedMember =
                                        updatedQueueMembersFromAction.find(
                                            (m) =>
                                                m.get('key') ===
                                                prevMember.get('key'),
                                        );
                                    return updatedMember
                                        ? prevMember.mergeDeep(updatedMember)
                                        : prevMember;
                                });

                            // Add new members that weren't in the previous list
                            const existingMemberKeys = mergedQueueMembers.map(
                                (queueMember) => queueMember.get('key'),
                            );
                            const newMembers =
                                updatedQueueMembersFromAction.filter(
                                    (queueMember) =>
                                        !existingMemberKeys.includes(
                                            queueMember.get('key'),
                                        ),
                                );

                            updatedQueueMembers = updatedQueueMembers.set(
                                queueKey,
                                mergedQueueMembers.concat(newMembers),
                            );
                        } else {
                            // If there are no previous queueMembers, set the new ones
                            updatedQueueMembers = updatedQueueMembers.set(
                                queueKey,
                                updatedQueueMembersFromAction,
                            );
                        }
                    });

                    return updatedQueueMembers;
                })
                .update('sortingData', (sortingData = Map()) => {
                    return sortingData.update('queues', (queues = Map()) => {
                        // First filter out queues that no longer exist
                        const filteredQueues = queues.filter(
                            (_queue, queueKey) => mapped.has(queueKey),
                        );
                        // Then update/add queues from the action data
                        return mapped.reduce((acc, item) => {
                            const ownExtensionIsMember = item
                                .get('queueMembers', List())
                                .some(
                                    (queueMember) =>
                                        ownExtensionKey &&
                                        queueMember.get('extensionKey') ===
                                            ownExtensionKey,
                                );
                            return acc.set(
                                item.get('key'),
                                Map({
                                    description: item.get('description'),
                                    key: item.get('key'),
                                    number: item.get('number'),
                                    // Flattened off the queue's own contact -- not its
                                    // members' -- so the PBX list can search on it. The
                                    // projection is deliberately light, so it takes the
                                    // one field rather than the whole contact. A response
                                    // without a contact (the reconnect refresh fetches
                                    // without one) keeps the keywords already held; a
                                    // contact without keywords means they were cleared,
                                    // since the API omits null fields. (AC-1943)
                                    keywords: item.has('contact')
                                        ? item.getIn(['contact', 'keywords'])
                                        : acc.getIn([
                                              item.get('key'),
                                              'keywords',
                                          ]),
                                    ownExtensionIsMember: ownExtensionIsMember,
                                }),
                            );
                        }, filteredQueues);
                    });
                });
        }
        case Actions.RESULT_GET_VOICEMAIL_ACTIONS: {
            const voiceMessageActionList = fromJS(action.voiceMessageActions);

            if (voiceMessageActionList.isEmpty()) {
                return state.deleteIn([
                    'voicemails',
                    action.voicemailKey,
                    'voiceMessageActions',
                    action.voiceMessageKey,
                ]);
            } else {
                return state.setIn(
                    [
                        'voicemails',
                        action.voicemailKey,
                        'voiceMessageActions',
                        action.voiceMessageKey,
                    ],
                    voiceMessageActionList,
                );
            }
        }
        case Actions.RESULT_CREATE_VOICEMAIL_ACTION: {
            return state.updateIn(
                [
                    'voicemails',
                    action.voicemailKey,
                    'voiceMessageActions',
                    action.voiceMessageKey,
                ],
                List(),
                (actionList) =>
                    actionList.push(fromJS(action.voiceMessageAction)),
            );
        }
        case Actions.RESULT_DELETE_VOICEMAIL_ACTION: {
            const updatedVoiceMessageActions = state
                .getIn(
                    [
                        'voicemails',
                        action.voicemailkey,
                        'voiceMessageActions',
                        action.voiceMessageKey,
                    ],
                    List(),
                )
                .update((actionList) => {
                    const actionIndex = actionList.find(
                        (action) => action.get('key') === action.callActionKey,
                    );
                    return actionList.delete(actionIndex);
                });

            if (updatedVoiceMessageActions.isEmpty()) {
                return state.deleteIn([
                    'voicemails',
                    action.voicemailKey,
                    'voiceMessageActions',
                    action.voiceMessageKey,
                ]);
            } else {
                return state.setIn(
                    [
                        'voicemails',
                        action.voicemailKey,
                        'voiceMessageActions',
                        action.voiceMessageKey,
                    ],
                    updatedVoiceMessageActions,
                );
            }
        }
        case Actions.RESULT_QUEUE_MEMBER_UPDATE: {
            const indexOfMember = state
                .getIn(['queueMembers', action.queueKey], List())
                .findIndex((member) => member.get('key') === action.memberKey);

            if (indexOfMember === -1) {
                return state;
            }

            const updatedQueueWaves = state
                .getIn(['queues', action.queueKey, 'queueWaves'], List())
                .map((queueWave) =>
                    queueWave.set(
                        'queueWaveMappingDTOs',
                        queueWave
                            .get('queueWaveMappingDTOs')
                            .map((queueWaveMapping) => {
                                const queueMember =
                                    queueWaveMapping.get('queueMember');
                                return queueWaveMapping.set(
                                    'queueMember',
                                    queueMember.get('key') === action.memberKey
                                        ? queueMember.mergeDeep(
                                              fromJS(action.data),
                                          )
                                        : queueMember,
                                );
                            }),
                    ),
                );

            return state
                .updateIn(
                    ['queueMembers', action.queueKey, indexOfMember],
                    Map(),
                    (prev) => prev.mergeDeep(fromJS(action.data)),
                )
                .setIn(
                    ['queues', action.queueKey, 'queueWaves'],
                    updatedQueueWaves,
                );
        }
        case Actions.RESULT_ADD_QUEUE_MEMBERS: {
            const ownExtensionKey = action.ownExtensionKey;
            const allQueueMembers = fromJS(action.data);
            return state
                .setIn(['queueMembers', action.queueKey], allQueueMembers)
                .updateIn(
                    [
                        'sortingData',
                        'queues',
                        action.queueKey,
                        'ownExtensionIsMember',
                    ],
                    () =>
                        !!allQueueMembers.find(
                            (member) =>
                                ownExtensionKey &&
                                member.get('extensionKey') === ownExtensionKey,
                        ),
                );
        }
        case Actions.RESULT_ADD_QUEUE_WAVE_MAPPINGS: {
            const existingQueueMembers = state.getIn(
                ['queueMembers', action.queueKey],
                List(),
            );
            const existingQueueMemberKeys = existingQueueMembers.map(
                (queueMember) => queueMember.get('key'),
            );

            const queueWaveMappings = fromJS(action.data);
            const queueMembersFromMappings = queueWaveMappings.map(
                (queueWaveMapping) => queueWaveMapping.get('queueMember'),
            );

            const newQueueMembers = queueMembersFromMappings.filter(
                (queueMember) =>
                    !existingQueueMemberKeys.contains(queueMember.get('key')),
            );
            const updatedQueueMembers =
                existingQueueMembers.concat(newQueueMembers);

            const indexOfWave = state
                .getIn(['queues', action.queueKey, 'queueWaves'])
                .findIndex(
                    (queueWave) => queueWave.get('key') === action.queueWaveKey,
                );

            return state
                .setIn(
                    [
                        'queues',
                        action.queueKey,
                        'queueWaves',
                        indexOfWave,
                        'queueWaveMappingDTOs',
                    ],
                    queueWaveMappings,
                )
                .setIn(['queueMembers', action.queueKey], updatedQueueMembers);
        }
        case Actions.RESULT_REMOVE_QUEUE_MEMBERS: {
            const ownExtensionKey = action.ownExtensionKey;
            const queueMembersWithoutRemoved = fromJS(action.data); // This is the list of queue members that are still members after the removal
            return state
                .setIn(
                    ['queueMembers', action.queueKey],
                    queueMembersWithoutRemoved,
                )
                .updateIn(
                    [
                        'sortingData',
                        'queues',
                        action.queueKey,
                        'ownExtensionIsMember',
                    ],
                    () =>
                        !!queueMembersWithoutRemoved.find(
                            (member) =>
                                ownExtensionKey &&
                                member.get('extensionKey') === ownExtensionKey,
                        ),
                );
        }
        case Actions.RESULT_REMOVE_QUEUE_WAVE_MAPPING: {
            const queueWaves = fromJS(action.data);
            const queueMemberKeys = queueWaves.flatMap((queueWave) =>
                queueWave
                    .get('queueWaveMappingDTOs')
                    .map((queueWaveMapping) =>
                        queueWaveMapping.get('queueMember').get('key'),
                    ),
            );

            const updatedQueueMembers = state
                .getIn(['queueMembers', action.queueKey])
                .filter((queueMember) =>
                    queueMemberKeys.contains(queueMember.get('key')),
                );

            return state
                .setIn(['queues', action.queueKey, 'queueWaves'], queueWaves)
                .setIn(['queueMembers', action.queueKey], updatedQueueMembers);
        }
        case Actions.RESULT_ALLOWED_QUEUE_MEMBERS: {
            return state.setIn(
                ['queues', action.key, 'allowedQueueMembers'],
                fromJS(action.data),
            );
        }
        case Actions.RESULT_REFER: {
            const referDTO = fromJS(action.data);

            return state.updateIn(['refers', action.key], Map(), (prev) =>
                prev.mergeDeep(referDTO),
            );
        }
        case Actions.RESULT_REFER_PROFILE: {
            return state.updateIn(['refers', action.key, 'activeProfile'], () =>
                fromJS(action.data),
            );
        }
        case Actions.RESULT_REFER_PROFILES: {
            return state.updateIn(['refers', action.key, 'profiles'], () =>
                fromJS(action.data),
            );
        }
        case Actions.RESULT_REFER_CALENDAR: {
            return state.updateIn(['refers', action.key, 'calendar'], () =>
                fromJS(action.data),
            );
        }
        case Actions.RESULT_REFER_OPENING_HOURS: {
            return state.setIn(
                ['refers', action.key, 'calendar', 'openingHours'],
                fromJS(action.data),
            );
        }
        case Actions.RESULT_REFERS: {
            const newRefers = fromJS(action.data);
            const mapped = listToMap(newRefers, (item) => item.get('key'));
            const updatedState = state.update('refers', (prevRefers = Map()) =>
                prevRefers.filter(keyIn(...mapped.keys())).mergeDeep(mapped),
            );
            return updatedState;
        }
        case Actions.RESULT_SHARED_VOICEMAILS: {
            const voicemailsReceived = fromJS(action.data);
            const mappedVoicemails = listToMap(voicemailsReceived, (item) =>
                item.get('key'),
            );

            const updatedMappedVoicemails = mappedVoicemails.map(
                (voicemail) => {
                    const voiceMessages = voicemail.get('voiceMessages', Map());
                    const mappedVoiceMessages = listToMap(
                        voiceMessages,
                        (item) => item.get('uniqueId'),
                    );
                    const orderedMappedVoiceMessages =
                        getVoiceMessagesSortedByReceivedDate(
                            mappedVoiceMessages,
                        );
                    return voicemail.set(
                        'voiceMessages',
                        orderedMappedVoiceMessages,
                    );
                },
            );

            /*
             * Some voicemail properties are not always included in the API response.
             * Therefore, we update and merge instead of doing a set, so that we don't lose any of the voicemail data that
             * is present in the state but not in the API response...
             **/
            const stateMerged = state.update('voicemails', (prev = Map()) =>
                prev.mergeDeep(updatedMappedVoicemails),
            );

            /*
             * ...however, a voicemail's voice messages should be overwritten from the API response.
             * The reason is that voice message(s) present in our store but not in the API response are messages that have probably been deleted by a colleague,
             * and we don't want to present non-existing voice messages.
             **/
            const stateWithUpdatedVoiceMessages = stateMerged.update(
                'voicemails',
                (voicemails) =>
                    voicemails.map((voicemail, key) =>
                        voicemail.set(
                            'voiceMessages',
                            updatedMappedVoicemails.getIn([
                                key,
                                'voiceMessages',
                            ]),
                        ),
                    ),
            );

            // If the voicemessages/state hasn't changed we want to make sure we return the same state to avoid rerender.
            return state
                .get('voicemails', Map())
                .equals(stateWithUpdatedVoiceMessages.get('voicemails', Map()))
                ? state
                : stateWithUpdatedVoiceMessages;
        }
        case Actions.RESULT_SHARED_VOICEMAIL: {
            const immutableData = fromJS(action.data);

            const voiceMessages = immutableData.get('voiceMessages', Map());
            const mappedVoiceMessages = listToMap(voiceMessages, (item) =>
                item.get('uniqueId'),
            );
            const orderedMappedVoiceMessages =
                getVoiceMessagesSortedByReceivedDate(mappedVoiceMessages);

            /*
             * Some voicemail properties are not always included in the API response.
             * Therefore, we update and merge instead of doing a set, so that we don't lose any of the voicemail data that
             * is present in the state but not in the API response.
             * However, the voicemail's voice messages should be overwritten from the API response.
             * The reason is that voice message(s) present in our store but not in the API response are messages that have probably been deleted by a colleague,
             * and we don't want to present non-existing voice messages.
             **/
            return state
                .updateIn(['voicemails', action.key], Map(), (voicemail) =>
                    voicemail.mergeDeep(immutableData),
                )
                .setIn(
                    ['voicemails', action.key, 'voiceMessages'],
                    orderedMappedVoiceMessages,
                );
        }
        case Actions.RESULT_SHARED_VOICEMESSAGE_DELETE: {
            return state.updateIn(
                ['voicemails', action.key, 'voiceMessages'],
                (messages) => {
                    return messages.filter(
                        (m) => m.get('uniqueId') !== action.data.uniqueId,
                    );
                },
            );
        }
        case Actions.RESULT_SHARED_VOICEMESSAGES_DELETE_ALL: {
            return state.setIn(
                ['voicemails', action.key, 'voiceMessages'],
                List(),
            );
        }
        case Actions.SET_SHARED_VOICEMESSAGE_READ: {
            return state.setIn(
                [
                    'voicemails',
                    action.voicemailKey,
                    'voiceMessages',
                    action.voiceMessageUniqueID,
                    'voiceMessageState',
                ],
                'read',
            );
        }
        case Actions.RESULT_CONFERENCE: {
            const indexOfConference = state
                .get('conferences')
                .findIndex(
                    (conference) => conference.get('key') === action.key,
                );
            return state.setIn(
                ['conferences', indexOfConference],
                fromJS(action.data),
            );
        }
        case Actions.RESULT_CONFERENCES: {
            return state.update('conferences', () => fromJS(action.data));
        }
        case Actions.RESULT_REFER_UPDATE_CALENDAR_EVENT: {
            return state.updateIn(
                ['refers', action.entityKey, 'calendar', 'events'],
                List(),
                (events) => {
                    return events.set(
                        events.findIndex(
                            (e) =>
                                e.get('key') ===
                                action.originalCalendarEventKey,
                        ),
                        fromJS(action.data),
                    );
                },
            );
        }
        case Actions.RESULT_REFER_REMOVE_CALENDAR_EVENT: {
            return state.updateIn(
                ['refers', action.entityKey, 'calendar', 'events'],
                (events) => {
                    return events.filter(
                        (e) => e.get('key') !== action.data.key,
                    );
                },
            );
        }
        case Actions.RESULT_REFER_ADD_CALENDAR_EVENT: {
            return state.updateIn(
                ['refers', action.entityKey, 'calendar', 'events'],
                (events) => events.push(fromJS(action.data)),
            );
        }

        case Actions.RESULT_QUEUE_UPDATE_CALENDAR_EVENT: {
            return state.updateIn(
                ['queues', action.entityKey, 'calendar', 'events'],
                List(),
                (events) => {
                    return events.set(
                        events.findIndex(
                            (e) =>
                                e.get('key') ===
                                action.originalCalendarEventKey,
                        ),
                        fromJS(action.data),
                    );
                },
            );
        }
        case Actions.RESULT_QUEUE_REMOVE_CALENDAR_EVENT: {
            return state.updateIn(
                ['queues', action.entityKey, 'calendar', 'events'],
                (events) => {
                    return events.filter(
                        (e) => e.get('key') !== action.data.key,
                    );
                },
            );
        }
        case Actions.RESULT_QUEUE_ADD_CALENDAR_EVENT: {
            return state.updateIn(
                ['queues', action.entityKey, 'calendar', 'events'],
                (events) => events.push(fromJS(action.data)),
            );
        }

        case Actions.RESULT_SHARED_EXTENSION: {
            return state.update('connections', Map(), () =>
                fromJS(action.data),
            );
        }

        case Actions.RESULT_QUEUE_PROFILE_UPDATE: {
            return state.updateIn(
                ['queues', action.entityKey, 'profiles'],
                (profiles) => {
                    return profiles.set(
                        profiles.findIndex(
                            (p) => p.get('key') === action.profileKey,
                        ),
                        fromJS(action.data),
                    );
                },
            );
        }

        default: {
            return state;
        }
    }
};

export default pbxReducer;
